/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useTheme } from 'styled-components'
import {
  XLSX,
  buildCorporateSheet,
  cellText,
  cellNumber,
  cellPercent,
  cellTotalLabel,
  cellTotalNumber,
} from '../../utils/excelCorporateStyle'
import {
  FaHistory,
  FaUserCheck,
  FaUserTimes,
  FaExclamationTriangle,
  FaEdit,
  FaCheck,
  FaSearch,
  FaFileExcel,
  FaTimes,
  FaSpinner,
  FaFilter,
  FaStore,
  FaCalendarAlt,
  FaInfoCircle,
  FaChevronLeft,
  FaChevronRight,
  FaBuilding,
  FaListUl,
  FaCheckCircle,
  FaCheckDouble,
} from 'react-icons/fa'
import {
  KpiGrid,
  KpiCard,
  TableWrapper,
  TableControls,
  SearchInputWrapper,
  ExportButton,
  TableScroll,
  StyledTable,
  PdvStatusBadge,
  JustificativaBadge,
  FilterChipGroup,
  FilterChip,
  PaginationWrapper,
  PageButton,
  ModalOverlay,
  ModalContent,
  ClientsModalContent,
  ModalTableScroll,
  ModalClientsTable,
  ModalStatsRow,
  ModalStatPill,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormLabel,
  StyledSelect,
  StyledTextArea,
  BatchActionBar,
} from './styles'

import api from '../../services/axios'
import {
  DistribuidorHistoricoItem,
  ClienteHistoricoItem,
  HistoricoPdvsKpis,
  HistoricoDistribuidoresResponse,
  HistoricoClientesResponse,
} from './types'

interface CoverageHistoryTabProps {
  distributorId?: number | string | null
  anoAtualPadrao?: number
  mesAtualPadrao?: number
}

const NOMES_MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
]

const MOTIVOS_SUGERIDOS = [
  'Empresa Encerrou Atividades / Fechou',
  'Inadimplência / Bloqueio Financeiro',
  'Migrou para Concorrente',
  'Troca de Ramo / Atividade',
  'Volume Abaixo do Pedido Mínimo',
  'Problemas Logísticos / Frete / Entrega',
  'Atendimento Direto / Indústria',
  'Preço / Condição Comercial Desfavorável',
  'PDV Não Localizado / Cadastro Incorreto',
  'Outros (Detalhar em Observação)',
]

// Formatação visual profissional de CNPJ / CPF
export function formatarCnpjCpf(valor?: string | null): string {
  if (!valor) return '—'
  const clean = valor.toString().replace(/\D/g, '')
  if (clean.length === 14) {
    return clean.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
  }
  if (clean.length === 11) {
    return clean.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
  }
  return valor
}

// Formatação amigável de Data do Sellout (YYYYMMDD ou YYYY-MM-DD -> DD/MM/YYYY)
export function formatarDataSellout(dataStr?: string | null): string {
  if (!dataStr) return 'Sem compras registradas'
  const limpo = dataStr.toString().trim()
  if (limpo === '19000101' || limpo === '00000000' || limpo === '—' || limpo === '') {
    return 'Sem compras registradas'
  }
  if (/^\d{8}$/.test(limpo)) {
    const ano = limpo.slice(0, 4)
    const mes = limpo.slice(4, 6)
    const dia = limpo.slice(6, 8)
    return `${dia}/${mes}/${ano}`
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(limpo)) {
    const [ano, mes, dia] = limpo.slice(0, 10).split('-')
    return `${dia}/${mes}/${ano}`
  }
  const d = new Date(limpo)
  if (!isNaN(d.getTime())) {
    return d.toLocaleDateString('pt-BR')
  }
  return limpo
}

export const CoverageHistoryTab: React.FC<CoverageHistoryTabProps> = ({
  distributorId,
  anoAtualPadrao = new Date().getFullYear(),
  mesAtualPadrao,
}) => {
  const theme = useTheme() as any
  const isLight = theme?.name === 'light' || theme?.title === 'light' || theme?.mode === 'light'

  // Estado da Lista Principal de Distribuidores
  const [loading, setLoading] = useState(false)
  const [distribuidores, setDistribuidores] = useState<DistribuidorHistoricoItem[]>([])
  const [kpis, setKpis] = useState<HistoricoPdvsKpis>({
    total_cadastrados_base: 0,
    ativos_ano_atual: 0,
    inativos_perdidos: 0,
    taxa_churn_pct: 0,
    total_justificados: 0,
    total_pendentes_justificativa: 0,
  })
  const [searchDist, setSearchDist] = useState('')

  // Estado do Modal de Clientes do Distribuidor
  const [modalDistOpen, setModalDistOpen] = useState(false)
  const [selectedDist, setSelectedDist] = useState<DistribuidorHistoricoItem | null>(null)
  const [clientes, setClientes] = useState<ClienteHistoricoItem[]>([])
  const [loadingClientes, setLoadingClientes] = useState(false)
  const [totalClientesCount, setTotalClientesCount] = useState(0)
  const [clientPage, setClientPage] = useState(1)
  const clientPageSize = 50

  // Filtros internos do Modal de Clientes
  const [searchCliente, setSearchCliente] = useState('')
  const [filtroStatusCliente, setFiltroStatusCliente] = useState<'todos' | 'inativo' | 'ativo'>('todos')
  const [filtroJustificadoCliente, setFiltroJustificadoCliente] = useState<'todos' | 'sim' | 'nao'>('todos')

  // Estado de Seleção em Lote de PDVs
  const [selectedCnpjs, setSelectedCnpjs] = useState<string[]>([])
  const [isLoteMode, setIsLoteMode] = useState(false)

  // Estado do Sub-Modal de Justificativa
  const [modalJustificarOpen, setModalJustificarOpen] = useState(false)
  const [selectedPdv, setSelectedPdv] = useState<ClienteHistoricoItem | null>(null)
  const [formMotivo, setFormMotivo] = useState('')
  const [formObservacao, setFormObservacao] = useState('')
  const [savingJustificativa, setSavingJustificativa] = useState(false)
  const [feedbackMessage, setFeedbackMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null)

  // 1. Carregar lista consolidada de distribuidores usando os filtros nativos da tela
  const carregarDistribuidores = useCallback(async () => {
    setLoading(true)
    try {
      const payload: any = {
        distId: distributorId ? Number(distributorId) : undefined,
        anoAtual: anoAtualPadrao,
        mesAtual: mesAtualPadrao,
      }

      const res = await api.post<HistoricoDistribuidoresResponse>('/rank/historico-pdvs', payload)
      if (res.data && res.data.success) {
        setDistribuidores(res.data.distribuidores || [])
        setKpis(res.data.kpis)
      }
    } catch (err) {
      console.error('Erro ao carregar histórico de distribuidores:', err)
    } finally {
      setLoading(false)
    }
  }, [distributorId, anoAtualPadrao, mesAtualPadrao])

  useEffect(() => {
    carregarDistribuidores()
  }, [carregarDistribuidores])

  // 2. Carregar lista de clientes do distribuidor selecionado no modal
  const carregarClientesDoDistribuidor = useCallback(async () => {
    if (!selectedDist) return
    setLoadingClientes(true)
    try {
      const payload: any = {
        distId: selectedDist.dist_id,
        anoAtual: anoAtualPadrao,
        mesAtual: mesAtualPadrao,
        search: searchCliente.trim() || undefined,
        status: filtroStatusCliente !== 'todos' ? filtroStatusCliente : undefined,
        justificado: filtroJustificadoCliente !== 'todos' ? filtroJustificadoCliente : undefined,
        page: clientPage,
        pageSize: clientPageSize,
      }

      const res = await api.post<HistoricoClientesResponse>('/rank/historico-pdvs/clientes', payload)
      if (res.data && res.data.success) {
        setClientes(res.data.data || [])
        setTotalClientesCount(res.data.totalCount || 0)
      }
    } catch (err) {
      console.error('Erro ao carregar clientes do distribuidor:', err)
    } finally {
      setLoadingClientes(false)
    }
  }, [selectedDist, anoAtualPadrao, mesAtualPadrao, searchCliente, filtroStatusCliente, filtroJustificadoCliente, clientPage])

  useEffect(() => {
    if (modalDistOpen && selectedDist) {
      carregarClientesDoDistribuidor()
    }
  }, [modalDistOpen, selectedDist, carregarClientesDoDistribuidor])



  // Filtragem de distribuidores na tabela principal
  const distribuidoresFiltrados = useMemo(() => {
    if (!searchDist.trim()) return distribuidores
    const term = searchDist.toLowerCase().trim()
    return distribuidores.filter(
      (d) =>
        d.distribuidor.toLowerCase().includes(term) ||
        d.cod_dist.toLowerCase().includes(term) ||
        d.regional.toLowerCase().includes(term) ||
        d.uf.toLowerCase().includes(term),
    )
  }, [distribuidores, searchDist])

  // Abertura do modal do distribuidor
  const handleOpenDistModal = (dist: DistribuidorHistoricoItem) => {
    setSelectedDist(dist)
    setClientPage(1)
    setSearchCliente('')
    setFiltroStatusCliente('todos')
    setFiltroJustificadoCliente('todos')
    setSelectedCnpjs([])
    setIsLoteMode(false)
    setModalDistOpen(true)
  }

  const handleCloseDistModal = () => {
    setModalDistOpen(false)
    setSelectedDist(null)
    setClientes([])
    setSelectedCnpjs([])
    setIsLoteMode(false)
  }

  // Lógica de seleção em lote de clientes
  const isAllPageSelected = useMemo(() => {
    if (clientes.length === 0) return false
    return clientes.every((c) => selectedCnpjs.includes(c.cnpj_cpf))
  }, [clientes, selectedCnpjs])

  const handleToggleSelectAllPage = () => {
    if (isAllPageSelected) {
      const pageCnpjs = new Set(clientes.map((c) => c.cnpj_cpf))
      setSelectedCnpjs((prev) => prev.filter((cnpj) => !pageCnpjs.has(cnpj)))
    } else {
      const pageCnpjs = clientes.map((c) => c.cnpj_cpf)
      setSelectedCnpjs((prev) => Array.from(new Set([...prev, ...pageCnpjs])))
    }
  }

  const handleToggleSelectPdv = (cnpj: string) => {
    setSelectedCnpjs((prev) =>
      prev.includes(cnpj) ? prev.filter((item) => item !== cnpj) : [...prev, cnpj],
    )
  }

  const handleClearSelection = () => {
    setSelectedCnpjs([])
  }

  // Abertura do sub-modal para justificativa individual
  const handleOpenJustificar = (pdv: ClienteHistoricoItem) => {
    setIsLoteMode(false)
    setSelectedPdv(pdv)
    setFormMotivo(pdv.motivo_justificativa || MOTIVOS_SUGERIDOS[0])
    setFormObservacao(pdv.observacao_justificativa || '')
    setFeedbackMessage(null)
    setModalJustificarOpen(true)
  }

  // Abertura do sub-modal para justificativa em lote
  const handleOpenJustificarLote = () => {
    if (selectedCnpjs.length === 0) return
    setIsLoteMode(true)
    setSelectedPdv(null)
    setFormMotivo(MOTIVOS_SUGERIDOS[0])
    setFormObservacao('')
    setFeedbackMessage(null)
    setModalJustificarOpen(true)
  }

  const handleCloseJustificar = () => {
    setModalJustificarOpen(false)
    setSelectedPdv(null)
    setIsLoteMode(false)
    setFeedbackMessage(null)
  }

  // Salvar justificativa do PDV (individual ou em lote)
  const handleSalvarJustificativa = async () => {
    if (!selectedDist) return
    if (!isLoteMode && !selectedPdv) return
    if (isLoteMode && selectedCnpjs.length === 0) return

    if (!formMotivo.trim()) {
      setFeedbackMessage({ text: 'Por favor, selecione um motivo.', type: 'error' })
      return
    }

    setSavingJustificativa(true)
    try {
      const payload = isLoteMode
        ? {
            cnpjs: selectedCnpjs,
            dist_id: selectedDist.dist_id,
            motivo: formMotivo,
            observacao: formObservacao,
          }
        : {
            cnpj_cpf_pdv: selectedPdv!.cnpj_cpf,
            dist_id: selectedDist.dist_id,
            motivo: formMotivo,
            observacao: formObservacao,
          }

      const res = await api.post('/rank/historico-pdvs/justificar', payload)

      if (res.data && res.data.success) {
        const msg = isLoteMode
          ? `Justificativa em lote salva com sucesso para ${selectedCnpjs.length} PDVs!`
          : 'Justificativa salva com sucesso!'
        setFeedbackMessage({ text: msg, type: 'success' })
        setTimeout(() => {
          handleCloseJustificar()
          setSelectedCnpjs([])
          carregarClientesDoDistribuidor()
          carregarDistribuidores()
        }, 900)
      } else {
        setFeedbackMessage({ text: res.data?.error || 'Erro ao salvar justificativa.', type: 'error' })
      }
    } catch (err: any) {
      console.error('Erro ao salvar justificativa:', err)
      setFeedbackMessage({
        text: err?.response?.data?.error || 'Erro de comunicação ao salvar justificativa.',
        type: 'error',
      })
    } finally {
      setSavingJustificativa(false)
    }
  }


  // Exportar Excel de Distribuidores
  const handleExportDistribuidoresExcel = () => {
    if (!distribuidores.length) return

    const headers = [
      'CÓDIGO',
      'DISTRIBUIDOR',
      'REGIONAL',
      'UF',
      'BASE CADASTRADA',
      'ATIVOS ANO ATUAL',
      'PERDIDOS (CHURN)',
      'TAXA DE PERDA',
      'JUSTIFICADOS',
      'PENDENTES',
    ]

    const dataRows = distribuidores.map((d) => [
      cellText(d.cod_dist, { center: true }),
      cellText(d.distribuidor),
      cellText(d.regional || '-', { center: true }),
      cellText(d.uf || '-', { center: true }),
      cellNumber(d.total_cadastrados),
      cellNumber(d.ativos_ano_atual),
      cellNumber(d.perdidos),
      cellPercent(Number(d.taxa_perda_pct || 0) / 100),
      cellNumber(d.total_justificados),
      cellNumber(d.total_pendentes),
    ])

    const totalCadastrados = distribuidores.reduce((acc, d) => acc + (d.total_cadastrados || 0), 0)
    const totalAtivos = distribuidores.reduce((acc, d) => acc + (d.ativos_ano_atual || 0), 0)
    const totalPerdidos = distribuidores.reduce((acc, d) => acc + (d.perdidos || 0), 0)
    const taxaGeral = totalCadastrados > 0 ? totalPerdidos / totalCadastrados : 0
    const totalJustificados = distribuidores.reduce((acc, d) => acc + (d.total_justificados || 0), 0)
    const totalPendentes = distribuidores.reduce((acc, d) => acc + (d.total_pendentes || 0), 0)

    const totalsRow = [
      cellTotalLabel('TOTAL GERAL'),
      cellTotalLabel(''),
      cellTotalLabel(''),
      cellTotalLabel(''),
      cellTotalNumber(totalCadastrados),
      cellTotalNumber(totalAtivos),
      cellTotalNumber(totalPerdidos),
      cellTotalNumber(taxaGeral, '#,##0.0%'),
      cellTotalNumber(totalJustificados),
      cellTotalNumber(totalPendentes),
    ]

    const wb = XLSX.utils.book_new()
    const ws = buildCorporateSheet({
      title: 'HISTÓRICO DE CHURN E RETENÇÃO POR DISTRIBUIDOR',
      subtitle: 'ANÁLISE CORPORATIVA DE RETENÇÃO E AUDITORIA DE PDVS',
      meta: `ANO: ${anoAtualPadrao} | TOTAL DE DISTRIBUIDORES: ${distribuidores.length}`,
      headers,
      dataRows,
      totalsRow,
    })

    XLSX.utils.book_append_sheet(wb, ws, 'Distribuidores_Perdas')
    XLSX.writeFile(wb, `Historico_Perdas_Distribuidores_${anoAtualPadrao}.xlsx`)
  }

  // Exportar Excel dos Clientes do Distribuidor Selecionado
  const handleExportClientesExcel = () => {
    if (!clientes.length || !selectedDist) return

    const headers = [
      'DISTRIBUIDOR',
      'CNPJ / CPF',
      'RAZÃO SOCIAL',
      'CIDADE',
      'UF',
      'DATA CADASTRO',
      'ÚLTIMA COMPRA SELLOUT',
      'STATUS ANO ATUAL',
      'MOTIVO JUSTIFICATIVA',
      'OBSERVAÇÃO JUSTIFICATIVA',
      'RESPONSÁVEL',
      'DATA JUSTIFICATIVA',
    ]

    const dataRows = clientes.map((c) => [
      cellText(selectedDist.distribuidor),
      cellText(formatarCnpjCpf(c.cnpj_cpf), { center: true }),
      cellText(c.razao_social || '-'),
      cellText(c.cidade || '-'),
      cellText(c.uf || '-', { center: true }),
      cellText(c.data_cadastro ? new Date(c.data_cadastro).toLocaleDateString('pt-BR') : '—', { center: true }),
      cellText(formatarDataSellout(c.ultima_compra), { center: true }),
      cellText(c.status === 'ativo' ? 'Ativo' : 'Inativo / Perdido', { center: true }),
      cellText(c.motivo_justificativa || 'Pendente'),
      cellText(c.observacao_justificativa || '-'),
      cellText(c.usuario_justificativa || '-'),
      cellText(c.updated_at_justificativa ? new Date(c.updated_at_justificativa).toLocaleDateString('pt-BR') : '-', { center: true }),
    ])

    const totalsRow = [
      cellTotalLabel('TOTAL DE REGISTROS'),
      cellTotalLabel(''),
      cellTotalNumber(clientes.length),
      cellTotalLabel(''),
      cellTotalLabel(''),
      cellTotalLabel(''),
      cellTotalLabel(''),
      cellTotalLabel(''),
      cellTotalLabel(''),
      cellTotalLabel(''),
      cellTotalLabel(''),
      cellTotalLabel(''),
    ]

    const wb = XLSX.utils.book_new()
    const ws = buildCorporateSheet({
      title: `AUDITORIA DE PDVS - ${selectedDist.distribuidor.toUpperCase()}`,
      subtitle: 'HISTÓRICO INDIVIDUAL DE RETENÇÃO, CHURN E JUSTIFICATIVAS DE CLIENTES',
      meta: `DISTRIBUIDOR: ${selectedDist.distribuidor} | ANO: ${anoAtualPadrao} | TOTAL DE CLIENTES: ${clientes.length}`,
      headers,
      dataRows,
      totalsRow,
    })

    XLSX.utils.book_append_sheet(wb, ws, 'PDVs_Distribuidor')
    XLSX.writeFile(wb, `PDVs_${selectedDist.distribuidor.replace(/[^a-zA-Z0-9]/g, '_')}_${anoAtualPadrao}.xlsx`)
  }

  const totalClientPages = Math.max(1, Math.ceil(totalClientesCount / clientPageSize))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Grade de KPIs Nacionais de Retenção & Churn */}
      <KpiGrid>
        <KpiCard $borderColor="#38bdf8">
          <div className="kpi-header">
            <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
              Base Histórica Cadastrada
            </span>
            <FaHistory style={{ color: '#38bdf8', fontSize: '18px' }} />
          </div>
          <div className="kpi-value" style={{ color: isLight ? '#0f172a' : '#ffffff', fontSize: '26px', fontWeight: 700 }}>
            {kpis.total_cadastrados_base.toLocaleString('pt-BR')}
          </div>
          <div className="kpi-footer" style={{ color: '#94a3b8', fontSize: '11px' }}>
            <span style={{ color: '#94a3b8' }}>Total de PDVs cadastrados na rede</span>
          </div>
        </KpiCard>

        <KpiCard $borderColor="#22c55e">
          <div className="kpi-header">
            <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
              PDVs Ativos em {anoAtualPadrao}
            </span>
            <FaUserCheck style={{ color: '#22c55e', fontSize: '18px' }} />
          </div>
          <div className="kpi-value" style={{ color: '#4ade80', fontSize: '26px', fontWeight: 700 }}>
            {kpis.ativos_ano_atual.toLocaleString('pt-BR')}
          </div>
          <div className="kpi-footer" style={{ color: '#94a3b8', fontSize: '11px' }}>
            <span style={{ color: '#94a3b8' }}>
              {kpis.total_cadastrados_base > 0
                ? `${((kpis.ativos_ano_atual / kpis.total_cadastrados_base) * 100).toFixed(1)}% de retenção ativa`
                : '0% de retenção'}
            </span>
          </div>
        </KpiCard>

        <KpiCard $borderColor="#ef4444">
          <div className="kpi-header">
            <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
              Perdas / Churn ({anoAtualPadrao})
            </span>
            <FaUserTimes style={{ color: '#ef4444', fontSize: '18px' }} />
          </div>
          <div className="kpi-value" style={{ color: '#f87171', fontSize: '26px', fontWeight: 700 }}>
            {kpis.inativos_perdidos.toLocaleString('pt-BR')}
          </div>
          <div className="kpi-footer" style={{ color: '#94a3b8', fontSize: '11px' }}>
            <span style={{ color: '#94a3b8' }}>Sem compras no ano corrente ({anoAtualPadrao})</span>
          </div>
        </KpiCard>

        <KpiCard $borderColor={kpis.taxa_churn_pct > 50 ? '#ef4444' : '#f59e0b'}>
          <div className="kpi-header">
            <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
              Taxa Média de Perda
            </span>
            <FaExclamationTriangle
              style={{ color: kpis.taxa_churn_pct > 50 ? '#ef4444' : '#f59e0b', fontSize: '18px' }}
            />
          </div>
          <div
            className="kpi-value"
            style={{ color: kpis.taxa_churn_pct > 50 ? '#f87171' : '#fbbf24', fontSize: '26px', fontWeight: 700 }}
          >
            {kpis.taxa_churn_pct.toFixed(1)}%
          </div>
          <div className="kpi-footer" style={{ color: '#94a3b8', fontSize: '11px' }}>
            <span style={{ color: '#94a3b8' }}>Média ponderada da rede</span>
          </div>
        </KpiCard>

        <KpiCard $borderColor="#0ea5e9">
          <div className="kpi-header">
            <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
              PDVs Justificados
            </span>
            <FaCheck style={{ color: '#0ea5e9', fontSize: '18px' }} />
          </div>
          <div className="kpi-value" style={{ color: '#38bdf8', fontSize: '26px', fontWeight: 700 }}>
            {kpis.total_justificados.toLocaleString('pt-BR')}
          </div>
          <div className="kpi-footer" style={{ color: '#94a3b8', fontSize: '11px' }}>
            <span style={{ color: '#94a3b8' }}>Motivos formalizados no sistema</span>
          </div>
        </KpiCard>

        <KpiCard $borderColor="#f59e0b">
          <div className="kpi-header">
            <span style={{ color: '#cbd5e1', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
              Pendentes de Justificativa
            </span>
            <FaEdit style={{ color: '#f59e0b', fontSize: '18px' }} />
          </div>
          <div className="kpi-value" style={{ color: '#fde68a', fontSize: '26px', fontWeight: 700 }}>
            {kpis.total_pendentes_justificativa.toLocaleString('pt-BR')}
          </div>
          <div className="kpi-footer" style={{ color: '#94a3b8', fontSize: '11px' }}>
            <span style={{ color: '#94a3b8' }}>Aguardando preenchimento comercial</span>
          </div>
        </KpiCard>
      </KpiGrid>

      {/* Tabela Principal: Distribuidores & Perdas */}
      <TableWrapper>
        <TableControls>
          <SearchInputWrapper style={{ maxWidth: '380px' }}>
            <FaSearch />
            <input
              type="text"
              placeholder="Buscar distribuidor, código ou regional..."
              value={searchDist}
              onChange={(e) => setSearchDist(e.target.value)}
            />
          </SearchInputWrapper>

          <ExportButton onClick={handleExportDistribuidoresExcel} title="Exportar tabela de distribuidores para Excel">
            <FaFileExcel /> Exportar Distribuidores
          </ExportButton>
        </TableControls>

        <TableScroll>
          <StyledTable>
            <thead>
              <tr>
                <th style={{ width: '80px' }}>Cód.</th>
                <th>Distribuidor</th>
                <th>Regional / UF</th>
                <th style={{ textAlign: 'right' }}>Base Cadastrada</th>
                <th style={{ textAlign: 'right' }}>Ativos em {anoAtualPadrao}</th>
                <th style={{ textAlign: 'right' }}>Perdidos (Churn)</th>
                <th style={{ textAlign: 'center' }}>% Perda</th>
                <th style={{ textAlign: 'center' }}>Justificados / Pendentes</th>
                <th style={{ textAlign: 'center' }}>Ação</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                    <FaSpinner className="spin" style={{ marginRight: '8px' }} /> Consolidando perdas por distribuidor...
                  </td>
                </tr>
              ) : distribuidoresFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                    Nenhum distribuidor encontrado para a busca informada.
                  </td>
                </tr>
              ) : (
                distribuidoresFiltrados.map((d) => {
                  const isAltaPerda = d.taxa_perda_pct >= 50
                  return (
                    <tr key={d.dist_id}>
                      <td style={{ color: isLight ? '#64748b' : '#94a3b8', fontFamily: 'monospace' }}>{d.cod_dist}</td>
                      <td style={{ fontWeight: '700', color: isLight ? '#0f172a' : '#f8fafc' }}>
                        {d.distribuidor || d.cod_dist || `Distribuidor ${d.dist_id}`}
                      </td>
                      <td style={{ color: isLight ? '#334155' : '#cbd5e1' }}>
                        {d.regional} / <span style={{ color: isLight ? '#15803d' : '#a6c10b', fontWeight: 'bold' }}>{d.uf}</span>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: '600', color: isLight ? '#0f172a' : '#f8fafc' }}>
                        {d.total_cadastrados.toLocaleString('pt-BR')}
                      </td>
                      <td style={{ textAlign: 'right', color: isLight ? '#16a34a' : '#4ade80', fontWeight: '700' }}>
                        {d.ativos_ano_atual.toLocaleString('pt-BR')}
                      </td>
                      <td style={{ textAlign: 'right', color: isLight ? '#dc2626' : '#f87171', fontWeight: '700' }}>
                        {d.perdidos.toLocaleString('pt-BR')}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span
                          style={{
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '700',
                            background: isAltaPerda ? (isLight ? '#fee2e2' : 'rgba(239, 68, 68, 0.18)') : (isLight ? '#fef3c7' : 'rgba(245, 158, 11, 0.18)'),
                            color: isAltaPerda ? (isLight ? '#b91c1c' : '#f87171') : (isLight ? '#b45309' : '#fbbf24'),
                            border: `1px solid ${isAltaPerda ? (isLight ? '#fca5a5' : 'rgba(239, 68, 68, 0.35)') : (isLight ? '#fde68a' : 'rgba(245, 158, 11, 0.35)')}`,
                          }}
                        >
                          {d.taxa_perda_pct.toFixed(1)}%
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', fontSize: '12px' }}>
                        <span style={{ color: isLight ? '#0284c7' : '#38bdf8', fontWeight: '600' }}>{d.total_justificados}</span> /{' '}
                        <span style={{ color: d.total_pendentes > 0 ? (isLight ? '#b45309' : '#f59e0b') : (isLight ? '#64748b' : '#94a3b8'), fontWeight: '600' }}>
                          {d.total_pendentes} pend.
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          onClick={() => handleOpenDistModal(d)}
                          style={{
                            background: 'rgba(166, 193, 11, 0.15)',
                            border: '1px solid #a6c10b',
                            color: '#a6c10b',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#a6c10b'
                            e.currentTarget.style.color = '#0f172a'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(166, 193, 11, 0.15)'
                            e.currentTarget.style.color = '#a6c10b'
                          }}
                          title={`Ver e justificar PDVs de ${d.distribuidor}`}
                        >
                          <FaStore /> Ver PDVs & Justificar
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </StyledTable>
        </TableScroll>
      </TableWrapper>

      {/* ========================================================================= */}
      {/* MODAL 1: Lista de Clientes do Distribuidor Selecionado (Modernizada)       */}
      {/* ========================================================================= */}
      {modalDistOpen && selectedDist && (
        <ModalOverlay onClick={handleCloseDistModal}>
          <ClientsModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '10px',
                    background: 'rgba(166, 193, 11, 0.15)',
                    border: '1px solid rgba(166, 193, 11, 0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#a6c10b',
                    fontSize: '18px',
                    flexShrink: 0,
                  }}
                >
                  <FaBuilding />
                </div>
                <div>
                  <h3 style={{ fontSize: '17px', fontWeight: 700, color: isLight ? '#0f172a' : '#ffffff', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Clientes & Churn: {selectedDist.distribuidor || selectedDist.cod_dist || `Distribuidor ${selectedDist.dist_id}`}
                    <span style={{ fontSize: '11px', background: isLight ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)', padding: '2px 8px', borderRadius: '4px', color: isLight ? '#475569' : '#94a3b8', fontWeight: 600 }}>
                      Cód: {selectedDist.cod_dist}
                    </span>
                  </h3>
                  <div style={{ fontSize: '12px', color: isLight ? '#64748b' : '#94a3b8', marginTop: '2px' }}>
                    Regional: <strong style={{ color: isLight ? '#0f172a' : '#cbd5e1' }}>{selectedDist.regional}</strong> | Base Principal: <strong style={{ color: isLight ? '#15803d' : '#a6c10b' }}>{selectedDist.uf}</strong>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <ExportButton onClick={handleExportClientesExcel} title="Exportar planilha Excel completa de clientes">
                  <FaFileExcel /> Exportar Clientes
                </ExportButton>
                <button className="close-btn" onClick={handleCloseDistModal} title="Fechar modal">
                  <FaTimes />
                </button>
              </div>
            </ModalHeader>

            {/* Painel de Indicadores Consolidados */}
            <div
              style={{
                padding: '14px 24px',
                background: 'rgba(255, 255, 255, 0.02)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <ModalStatsRow>
                <ModalStatPill $variant="neutral">
                  <span className="stat-label">Base Histórica</span>
                  <span className="stat-value">{selectedDist.total_cadastrados.toLocaleString('pt-BR')} PDVs</span>
                </ModalStatPill>

                <ModalStatPill $variant="success">
                  <span className="stat-label">Ativos em {anoAtualPadrao}</span>
                  <span className="stat-value">{selectedDist.ativos_ano_atual.toLocaleString('pt-BR')} PDVs</span>
                </ModalStatPill>

                <ModalStatPill $variant="danger">
                  <span className="stat-label">Perdas (Sem compras em {anoAtualPadrao})</span>
                  <span className="stat-value">
                    {selectedDist.perdidos.toLocaleString('pt-BR')} PDVs ({selectedDist.taxa_perda_pct.toFixed(1)}%)
                  </span>
                </ModalStatPill>
              </ModalStatsRow>
            </div>

            {/* Barra de Busca e Filtros Rápidos */}
            <div
              style={{
                padding: '14px 24px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                background: 'rgba(0, 0, 0, 0.15)',
              }}
            >
              <div style={{ display: 'flex', gap: '10px' }}>
                <SearchInputWrapper style={{ flex: 1, maxWidth: '100%' }}>
                  <FaSearch />
                  <input
                    type="text"
                    placeholder="Buscar por CNPJ, Razão Social ou Cidade..."
                    value={searchCliente}
                    onChange={(e) => {
                      setSearchCliente(e.target.value)
                      setClientPage(1)
                    }}
                  />
                  {searchCliente && (
                    <button
                      onClick={() => setSearchCliente('')}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#94a3b8',
                        cursor: 'pointer',
                        padding: '4px',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      title="Limpar busca"
                    >
                      <FaTimes />
                    </button>
                  )}
                </SearchInputWrapper>
              </div>

              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                <FilterChipGroup>
                  <span style={{ fontSize: '11px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, textTransform: 'uppercase' }}>
                    <FaFilter /> Status:
                  </span>
                  <FilterChip
                    $active={filtroStatusCliente === 'todos'}
                    onClick={() => {
                      setFiltroStatusCliente('todos')
                      setClientPage(1)
                    }}
                  >
                    Todos
                  </FilterChip>
                  <FilterChip
                    $active={filtroStatusCliente === 'inativo'}
                    $variant="danger"
                    onClick={() => {
                      setFiltroStatusCliente('inativo')
                      setClientPage(1)
                    }}
                  >
                    Perdidos (Churn) ({selectedDist.perdidos})
                  </FilterChip>
                  <FilterChip
                    $active={filtroStatusCliente === 'ativo'}
                    $variant="success"
                    onClick={() => {
                      setFiltroStatusCliente('ativo')
                      setClientPage(1)
                    }}
                  >
                    Ativos ({selectedDist.ativos_ano_atual})
                  </FilterChip>
                </FilterChipGroup>

                <FilterChipGroup>
                  <span style={{ fontSize: '11px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600, textTransform: 'uppercase' }}>
                    <FaEdit /> Justificativa:
                  </span>
                  <FilterChip
                    $active={filtroJustificadoCliente === 'todos'}
                    onClick={() => {
                      setFiltroJustificadoCliente('todos')
                      setClientPage(1)
                    }}
                  >
                    Todas
                  </FilterChip>
                  <FilterChip
                    $active={filtroJustificadoCliente === 'nao'}
                    $variant="warning"
                    onClick={() => {
                      setFiltroJustificadoCliente('nao')
                      setClientPage(1)
                    }}
                  >
                    Pendentes ({selectedDist.total_pendentes})
                  </FilterChip>
                  <FilterChip
                    $active={filtroJustificadoCliente === 'sim'}
                    $variant="info"
                    onClick={() => {
                      setFiltroJustificadoCliente('sim')
                      setClientPage(1)
                    }}
                  >
                    Justificados ({selectedDist.total_justificados})
                  </FilterChip>
                </FilterChipGroup>
              </div>
            </div>

            {/* Barra de Ação em Lote (Quando houver PDVs selecionados) */}
            {selectedCnpjs.length > 0 && (
              <BatchActionBar style={{ margin: '14px 24px 0 24px' }}>
                <div className="batch-info">
                  <FaCheckDouble />
                  <span>
                    <span className="badge-count">{selectedCnpjs.length}</span> PDV(s) selecionado(s) para justificativa
                  </span>
                </div>
                <div className="batch-actions">
                  <button
                    type="button"
                    className="btn-batch-action"
                    onClick={handleOpenJustificarLote}
                    title="Justificar todos os PDVs marcados de uma vez"
                  >
                    <FaEdit /> Justificar Selecionados em Lote ({selectedCnpjs.length})
                  </button>
                  <button
                    type="button"
                    className="btn-clear"
                    onClick={handleClearSelection}
                    title="Desmarcar todos"
                  >
                    <FaTimes /> Desmarcar
                  </button>
                </div>
              </BatchActionBar>
            )}

            {/* Tabela de Clientes com Rolagem Moderna */}
            <ModalTableScroll style={{ marginTop: selectedCnpjs.length > 0 ? '10px' : '0' }}>
              <ModalClientsTable>
                <thead>
                  <tr>
                    <th style={{ width: '46px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={isAllPageSelected}
                        onChange={handleToggleSelectAllPage}
                        title={isAllPageSelected ? 'Desmarcar todos desta página' : 'Selecionar todos desta página'}
                        style={{
                          cursor: 'pointer',
                          width: '16px',
                          height: '16px',
                          accentColor: '#a6c10b',
                          verticalAlign: 'middle',
                        }}
                      />
                    </th>
                    <th style={{ width: '180px' }}>Documento (CNPJ/CPF)</th>
                    <th>Cliente / Razão Social</th>
                    <th style={{ width: '200px' }}>Praça (Cidade / UF)</th>
                    <th style={{ textAlign: 'center', width: '180px' }}>Última Compra (Sellout)</th>
                    <th style={{ textAlign: 'center', width: '150px' }}>Status em {anoAtualPadrao}</th>
                    <th style={{ textAlign: 'center', width: '220px' }}>Justificativa de Churn</th>
                    <th style={{ textAlign: 'center', width: '110px' }}>Ação</th>
                  </tr>
                </thead>

                <tbody>
                  {loadingClientes ? (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: '50px 20px', color: '#94a3b8' }}>
                        <FaSpinner className="spin" style={{ marginRight: '8px', fontSize: '18px' }} />
                        Carregando clientes do distribuidor...
                      </td>
                    </tr>
                  ) : clientes.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: '50px 20px', color: '#94a3b8' }}>
                        Nenhum cliente encontrado para os filtros selecionados.
                      </td>
                    </tr>
                  ) : (
                    clientes.map((pdv) => {
                      const isAtivo = pdv.status === 'ativo'
                      const temJustificativa = !!pdv.motivo_justificativa
                      const cleanDigits = (pdv.cnpj_cpf || '').replace(/\D/g, '')
                      const isCpf = cleanDigits.length === 11
                      const isSelected = selectedCnpjs.includes(pdv.cnpj_cpf)

                      return (
                        <tr
                          key={pdv.cnpj_cpf}
                          style={{
                            background: isSelected ? 'rgba(166, 193, 11, 0.08)' : undefined,
                            transition: 'background 0.15s ease',
                          }}
                        >
                          <td style={{ textAlign: 'center', width: '46px' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleSelectPdv(pdv.cnpj_cpf)}
                              title={isSelected ? 'Desmarcar PDV' : 'Selecionar PDV'}
                              style={{
                                cursor: 'pointer',
                                width: '16px',
                                height: '16px',
                                accentColor: '#a6c10b',
                                verticalAlign: 'middle',
                              }}
                            />
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span style={{ color: isLight ? '#0f172a' : '#f1f5f9', fontWeight: 600, fontSize: '12px', letterSpacing: '0.4px' }}>
                                {formatarCnpjCpf(pdv.cnpj_cpf)}
                              </span>
                              <span style={{ fontSize: '10px', color: '#64748b' }}>
                                {isCpf ? 'CPF (Pessoa Física)' : 'CNPJ (Pessoa Jurídica)'}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span
                              style={{
                                color: isLight ? '#0f172a' : '#ffffff',
                                fontWeight: 600,
                                fontSize: '13px',
                                display: 'block',
                                maxWidth: '320px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={pdv.razao_social}
                            >
                              {pdv.razao_social}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ color: isLight ? '#334155' : '#cbd5e1', fontSize: '12px' }}>
                                {pdv.cidade || 'Não informada'}
                              </span>
                              <span
                                style={{
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  background: 'rgba(166, 193, 11, 0.15)',
                                  border: '1px solid rgba(166, 193, 11, 0.35)',
                                  color: '#a6c10b',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                }}
                              >
                                {pdv.uf || '—'}
                              </span>
                            </div>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {pdv.ultima_compra && pdv.ultima_compra !== '19000101' ? (
                              <span
                                style={{
                                  color: '#e2e8f0',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  background: 'rgba(255, 255, 255, 0.04)',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  border: '1px solid rgba(255, 255, 255, 0.08)',
                                }}
                              >
                                <FaCalendarAlt style={{ color: '#38bdf8', fontSize: '11px' }} />
                                {formatarDataSellout(pdv.ultima_compra)}
                              </span>
                            ) : (
                              <span style={{ color: '#64748b', fontSize: '11px', fontStyle: 'italic' }}>
                                Sem compras no sellout
                              </span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <PdvStatusBadge $status={pdv.status}>
                              <span className="dot" />
                              {isAtivo ? 'Ativo' : 'Perdido'}
                            </PdvStatusBadge>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <JustificativaBadge
                              $justificado={temJustificativa}
                              onClick={() => handleOpenJustificar(pdv)}
                              title={
                                temJustificativa
                                  ? `${pdv.motivo_justificativa} (${pdv.usuario_justificativa || 'Sistema'})`
                                  : 'Clique para justificar o motivo de inatividade'
                              }
                            >
                              {temJustificativa ? (
                                <FaCheckCircle style={{ color: '#38bdf8', fontSize: '12px' }} />
                              ) : (
                                <FaEdit style={{ color: '#f59e0b', fontSize: '12px' }} />
                              )}
                              <span>{temJustificativa ? pdv.motivo_justificativa : '+ Justificar Motivo'}</span>
                            </JustificativaBadge>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              onClick={() => handleOpenJustificar(pdv)}
                              style={{
                                background: 'rgba(255, 255, 255, 0.05)',
                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                color: '#e2e8f0',
                                padding: '5px 12px',
                                borderRadius: '8px',
                                fontSize: '11px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)'
                                e.currentTarget.style.borderColor = '#a6c10b'
                                e.currentTarget.style.color = '#ffffff'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'
                                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.12)'
                                e.currentTarget.style.color = '#e2e8f0'
                              }}
                            >
                              <FaEdit /> {temJustificativa ? 'Revisar' : 'Justificar'}
                            </button>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </ModalClientsTable>
            </ModalTableScroll>

            {/* Paginação do Modal */}
            <PaginationWrapper>
              <div className="page-info">
                Exibindo <strong>{clientes.length > 0 ? (clientPage - 1) * clientPageSize + 1 : 0}</strong> a{' '}
                <strong>{Math.min(clientPage * clientPageSize, totalClientesCount)}</strong> de{' '}
                <strong>{totalClientesCount.toLocaleString('pt-BR')}</strong> PDVs do distribuidor
              </div>
              <div className="btn-group">
                <PageButton
                  disabled={clientPage <= 1 || loadingClientes}
                  onClick={() => setClientPage((p) => Math.max(1, p - 1))}
                >
                  <FaChevronLeft style={{ marginRight: '4px' }} /> Anterior
                </PageButton>
                <span style={{ fontSize: '12px', color: '#cbd5e1', padding: '0 8px', fontWeight: 600 }}>
                  Página {clientPage} de {totalClientPages}
                </span>
                <PageButton
                  disabled={clientPage >= totalClientPages || loadingClientes}
                  onClick={() => setClientPage((p) => Math.min(totalClientPages, p + 1))}
                >
                  Próxima <FaChevronRight style={{ marginLeft: '4px' }} />
                </PageButton>
              </div>
            </PaginationWrapper>
          </ClientsModalContent>
        </ModalOverlay>
      )}

      {/* ========================================================================= */}
      {/* SUB-MODAL 2: Formulário para Justificar Motivo de Perda do PDV            */}
      {/* ========================================================================= */}
      {modalJustificarOpen && selectedDist && (selectedPdv || isLoteMode) && (
        <ModalOverlay onClick={handleCloseJustificar} style={{ zIndex: 10000 }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>
                {isLoteMode ? (
                  <>
                    <FaCheckDouble style={{ color: '#a6c10b' }} /> Justificativa em Lote ({selectedCnpjs.length} PDVs)
                  </>
                ) : (
                  <>
                    <FaInfoCircle /> Justificativa de Perda / Churn
                  </>
                )}
              </h3>
              <button className="close-btn" onClick={handleCloseJustificar}>
                <FaTimes />
              </button>
            </ModalHeader>

            <ModalBody>
              {isLoteMode ? (
                <div
                  className="pdv-info-card"
                  style={{
                    borderLeft: '4px solid #a6c10b',
                    background: 'rgba(166, 193, 11, 0.08)',
                    padding: '14px 16px',
                  }}
                >
                  <div
                    className="pdv-name"
                    style={{
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: '14px',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <FaCheckDouble style={{ color: '#a6c10b', fontSize: '16px' }} />
                    Aplicando justificativa para {selectedCnpjs.length} PDV(s) selecionado(s)
                  </div>
                  <div className="pdv-details" style={{ color: '#cbd5e1', fontSize: '12px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <span>
                      <strong style={{ color: '#f8fafc' }}>Distribuidor:</strong> {selectedDist.distribuidor} ({selectedDist.cod_dist})
                    </span>
                    <span>
                      <strong style={{ color: '#f8fafc' }}>Regional:</strong> {selectedDist.regional} / {selectedDist.uf}
                    </span>
                    <span>
                      <strong style={{ color: '#f8fafc' }}>Ano de Referência:</strong> {anoAtualPadrao}
                    </span>
                  </div>
                  <div style={{ marginTop: '8px', fontSize: '11px', color: '#94a3b8', lineHeight: 1.4 }}>
                    O motivo e a observação abaixo serão salvos simultaneamente para todos os {selectedCnpjs.length} PDVs marcados.
                  </div>
                </div>
              ) : selectedPdv ? (
                <div className="pdv-info-card">
                  <div className="pdv-name" style={{ color: '#ffffff', fontWeight: 700, fontSize: '14px', marginBottom: '6px' }}>
                    {selectedPdv.razao_social}
                  </div>
                  <div className="pdv-details" style={{ color: '#cbd5e1', fontSize: '11px' }}>
                    <span style={{ color: '#cbd5e1' }}>
                      <strong style={{ color: '#f8fafc' }}>CNPJ/CPF:</strong> {formatarCnpjCpf(selectedPdv.cnpj_cpf)}
                    </span>
                    <span style={{ color: '#cbd5e1' }}>
                      <strong style={{ color: '#f8fafc' }}>Praça:</strong> {selectedPdv.cidade}/{selectedPdv.uf}
                    </span>
                    <span style={{ color: '#cbd5e1' }}>
                      <strong style={{ color: '#f8fafc' }}>Distribuidor:</strong> {selectedDist.distribuidor}
                    </span>
                  </div>
                  <div className="pdv-details" style={{ marginTop: '6px', color: '#cbd5e1', fontSize: '11px' }}>
                    <span style={{ color: '#cbd5e1' }}>
                      <FaCalendarAlt style={{ marginRight: '4px', color: '#38bdf8' }} />
                      Última Compra (Sellout): {formatarDataSellout(selectedPdv.ultima_compra)}
                    </span>
                    <span style={{ color: '#cbd5e1' }}>
                      Status:{' '}
                      <strong style={{ color: selectedPdv.status === 'ativo' ? '#4ade80' : '#f87171' }}>
                        {selectedPdv.status === 'ativo' ? 'Ativo em ' + anoAtualPadrao : 'Inativo / Perdido'}
                      </strong>
                    </span>
                  </div>
                </div>
              ) : null}

              <div>
                <FormLabel htmlFor="motivo-select">Motivo Principal da Perda / Inatividade *</FormLabel>
                <StyledSelect
                  id="motivo-select"
                  value={formMotivo}
                  onChange={(e) => setFormMotivo(e.target.value)}
                >
                  <option value="" disabled>
                    Selecione um motivo...
                  </option>
                  {MOTIVOS_SUGERIDOS.map((mot) => (
                    <option key={mot} value={mot}>
                      {mot}
                    </option>
                  ))}
                </StyledSelect>
              </div>

              <div>
                <FormLabel htmlFor="observacao-textarea">Observações Adicionais / Histórico Comercial</FormLabel>
                <StyledTextArea
                  id="observacao-textarea"
                  placeholder="Descreva detalhes específicos da perda, contato feito pela equipe comercial ou previsão de reativação..."
                  value={formObservacao}
                  onChange={(e) => setFormObservacao(e.target.value)}
                  maxLength={500}
                />
                <span style={{ fontSize: '11px', color: '#64748b', display: 'block', textAlign: 'right' }}>
                  {formObservacao.length}/500 caracteres
                </span>
              </div>

              {feedbackMessage && (
                <div
                  style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    background:
                      feedbackMessage.type === 'success'
                        ? 'rgba(34, 197, 94, 0.15)'
                        : 'rgba(239, 68, 68, 0.15)',
                    color: feedbackMessage.type === 'success' ? '#86efac' : '#fca5a5',
                    border:
                      feedbackMessage.type === 'success'
                        ? '1px solid rgba(34, 197, 94, 0.4)'
                        : '1px solid rgba(239, 68, 68, 0.4)',
                  }}
                >
                  {feedbackMessage.text}
                </div>
              )}
            </ModalBody>

            <ModalFooter>
              <button className="btn-cancel" onClick={handleCloseJustificar} disabled={savingJustificativa}>
                Cancelar
              </button>
              <button
                className="btn-save"
                onClick={handleSalvarJustificativa}
                disabled={savingJustificativa || !formMotivo.trim()}
              >
                {savingJustificativa ? (
                  <>
                    <FaSpinner className="spin" /> Gravando...
                  </>
                ) : (
                  <>
                    <FaCheck />{' '}
                    {isLoteMode
                      ? `Salvar Justificativa para ${selectedCnpjs.length} PDVs`
                      : 'Salvar Justificativa'}
                  </>
                )}
              </button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </div>
  )
}

